import { UseMutationResult } from '@tanstack/react-query';
import { Credit, Debit } from 'interfaces';
import { MRT_Row, MRT_TableInstance } from 'mantine-react-table';

export interface UpdateMutationDataParams<T> {
  row: MRT_Row<T>;
  values: T;
  table: MRT_TableInstance<T>;
  mutation?: UseMutationResult<T, Error>;
}

export interface UpdateClaimMutationDataParams<T> {
  row: MRT_Row<T>;
  values: {
    newData: Partial<T>;
    credits?: Credit[];
    debits?: Debit[];
    dirtyFields?: {
      [key: string]: boolean;
    };
  };
  table: MRT_TableInstance<T>;
  mutation?: UseMutationResult<T, Error>;
}

export const updateClaimMutationData = async <T>({
  values,
  table,
  mutation,
}: UpdateClaimMutationDataParams<T>): Promise<T> => {
  const updatedObject = { ...values } as T;
  const result = await mutation?.mutateAsync(updatedObject);
  table.setEditingRow(null);
  return result as T;
};

export const updateMutationData = async <T>({
  row,
  values,
  table,
  mutation,
}: UpdateMutationDataParams<T>): Promise<void> => {
  const updatedObject = { ...row.original, ...values } as T;
  await mutation.mutateAsync(updatedObject);
  table.setEditingRow(null);
  return null;
};

export interface CreateMutationDataParams<T> {
  values: T;
  table?: MRT_TableInstance<T>;
  mutation?: UseMutationResult<T, Error>;
}

export const createMutationData = async <T>({
  table,
  values,
  mutation,
}: CreateMutationDataParams<T>): Promise<void> => {
  const newObject = { ...values } as T;
  if (mutation) await mutation.mutateAsync(newObject);
  if (table) table.setCreatingRow(null);
  return null;
};

export interface BulkCreateMutationDataParams<T> {
  values: T[];
  siteID: string;
  createdBy: string;
  mutation?: UseMutationResult<unknown, Error>;
  table?: MRT_TableInstance<T>;
}

export const createBulkMutationData = async <T>({
  siteID,
  createdBy,
  values,
  table,
  mutation,
}: BulkCreateMutationDataParams<T>): Promise<void> => {
  if (mutation) await mutation.mutateAsync({ siteID, values, createdBy });
  if (table) table.setCreatingRow(null);
};
